import {IconButton} from '@mui/material'
import {List, Map} from 'immutable'
import {useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {deleteResource} from 'redux-json-api'
import PropTypes from 'prop-types'

import AddDeputyModal from '../../corp_max_deputies/add/AddDeputyModal'
import AddStoryDrawer from './AddStoryDrawer'
import Button from '../../../../../shared_components/mui_base_components/button/Button'
import DeleteIcon from '../../../../../shared_assets/v2/DeleteIcon'
import EditIcon from '../../../../../shared_assets/v2/EditIcon'
import FileUploadActions from '../../../../../shared_actions/FileUploadActions'
import ManagePermissionsContainer from '../../permissions/specific_item/ManagePermissionsContainer'

import {CaretIcon, MicIcon} from './icons'
import {scheduleLifeStoryDeletion} from './lifeStoryPendingDeletion'
import refreshVaultItemResponses from './lifeStoryVaultRefresh'
import {LIFE_STORY_CHAPTERS} from './lifeStoryContent'
import {
  answeredStories,
  ETHICAL_WILL_ITEM_ID,
  formatDuration,
  lifeStoryStoryPath,
  storiesByChapter,
  storyCountLabel
} from './lifeStoryTools'
import {findFirmConfigByFirmOwnership} from '../../../../../lib/plan_data/userData'
import {monthDayYear} from '../../../../../lib/dateTools'
import {noop} from '../../../../../lib/tools'
import {useDeputyData, useEverplanData} from '../../../../../lib/hooks'
import routerUtils from '../../../../../lib/routerUtils'
import Logger from '../../../../../lib/NewLogger'

import './reviewScreen.scss'

const KebabIcon = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
    <circle cx='12' cy='5' r='1.8' />
    <circle cx='12' cy='12' r='1.8' />
    <circle cx='12' cy='19' r='1.8' />
  </svg>
)

const PdfIcon = () => (
  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
    <path d='M6 2h9l5 5v15H6z' />
    <path d='M15 2v5h5' />
  </svg>
)

// Untouched-chapter tiles shown before "More chapters" expands the grid
// (mock keeps the first row visible and folds the rest into tile-extra).
const VISIBLE_UNTOUCHED_CHAPTERS = 3

const isAudioFile = filename => ['mp3', 'm4a', 'webm', 'wav'].includes((filename || '').split('.').pop().toLowerCase())

const isPdfFile = filename => (filename || '').split('.').pop().toLowerCase() === 'pdf'

/**
 * In-flow review of the story (mock's review stage): everything written so
 * far grouped by chapter with Revisit links, tiles to begin untouched
 * chapters, and the published story's actions — preview, share with
 * deputies, download the PDF and recordings, or delete everything.
 */
const ReviewScreen = props => {
  const dispatch = useDispatch()

  const [menuOpen, setMenuOpen] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [contactToAddAsDeputy, setContactToAddAsDeputy] = useState(Map())
  const [storyPendingDeletion, setStoryPendingDeletion] = useState(null)
  const [showAddDeputyModal, setShowAddDeputyModal] = useState(false)
  const [showAllChapters, setShowAllChapters] = useState(false)
  // The chapter whose "+ Add a story" drawer is open; one at a time.
  const [addStoryChapterKey, setAddStoryChapterKey] = useState(null)
  const menuRef = useRef(null)

  const everplanId = props.userConfig.get('everplan-id')
  const {itemResponses, refetchItemResponses, newContacts} = useEverplanData(everplanId)
  const {deputies} = useDeputyData(everplanId)

  const openAddDeputyModal = (contact = Map()) => {
    setContactToAddAsDeputy(contact)
    setShowAddDeputyModal(true)
  }

  const closeAddDeputyModal = () => {
    setContactToAddAsDeputy(Map())
    setShowAddDeputyModal(false)
  }

  const firmConfig = useSelector(state => findFirmConfigByFirmOwnership({
    firmOwnerships: state.api['firm-ownerships'],
    firmConfigs: state.api['firm-configs'],
    userConfig: props.userConfig
  }))

  // The publish that brought the user here created or refreshed the vault
  // response group, so the cached item responses are stale until re-fetched.
  useEffect(() => { refetchItemResponses() }, [])

  useEffect(() => {
    if (!menuOpen) return

    const closeOnOutsideClick = event => {
      if (menuRef.current && !menuRef.current.contains(event.target))
        setMenuOpen(false)
    }

    const closeOnEscape = event => {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    document.addEventListener('click', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)

    return () => {
      document.removeEventListener('click', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [menuOpen])

  const answered = answeredStories(props.stories)
  const chapters = storiesByChapter(props.stories)
  const answeredChapterKeys = chapters.map(group => group.chapter.key)
  const untouchedChapters = LIFE_STORY_CHAPTERS.filter(chapter => !answeredChapterKeys.includes(chapter.key))

  const firstGroupId = props.ethicalWill ? props.ethicalWill.get('first-group-id') : null
  const itemResponse = itemResponses.find(response => response.get('item-id') === ETHICAL_WILL_ITEM_ID) || null

  const fileResponse = itemResponse && firstGroupId ?
    itemResponse.get('responses', List()).find(response => (
      response.get('group-id', List()).includes(firstGroupId) && response.get('element-type') === 'FILE'
    )) :
    null
  const files = fileResponse ? (fileResponse.getIn(['value', 'data']) || List()).toJS() : []
  const pdfFile = files.find(file => isPdfFile(file.name))
  const audioFiles = files.filter(file => isAudioFile(file.name))

  const startedDate = monthDayYear(props.ethicalWill ? props.ethicalWill.get('created-at') : new Date())

  const downloadPdf = () => {
    setMenuOpen(false)
    Logger.log({name: 'downloaded_life_story_pdf', payload: {context: 'life_story_review', type: 'vault'}})
    FileUploadActions.downloadFile(pdfFile.id, pdfFile.name)
    props.toast(`Downloading ${pdfFile.name}\u2026`)
  }

  const downloadAudio = () => {
    setMenuOpen(false)
    Logger.log({name: 'downloaded_life_story_audio', payload: {context: 'life_story_review', type: 'vault'}})
    audioFiles.forEach(file => FileUploadActions.downloadFile(file.id, file.name))
    props.toast(`Downloading ${audioFiles.length === 1 ? audioFiles[0].name : `${audioFiles.length} audio files`}\u2026`)
  }

  // Deferred so the undo snackbar (mock's undo bar) can take it back: the
  // DELETE only fires once the undo window passes. The vault refresh rides in
  // performDelete so the pending state (which hides the record everywhere)
  // holds until the category page's cache reflects the deletion.
  const deleteStory = () => {
    setConfirmingDelete(false)

    scheduleLifeStoryDeletion({
      ethicalWillId: props.ethicalWillId,
      firstGroupId,
      message: 'Your story has been deleted.',
      performDelete: () => dispatch(deleteResource({type: 'ethical-wills', id: props.ethicalWillId}))
        .then(() => Logger.log({name: 'deleted_life_story', payload: {context: 'life_story_review', type: 'vault'}}))
        .then(() => refreshVaultItemResponses(everplanId))
    })

    // Don't goBack: the will is still in the store until the undo window ends,
    // so history can re-open this same story and leave the user stuck until DELETE.
    props.exit({afterDelete: true})
  }

  // Deleting the last remaining story counts as removing the entire story
  // (AC 6.3): it goes through the full-deletion path — deferred DELETE with
  // the undo bar, back to the not-started hero — instead of leaving an empty
  // shell of a story behind.
  const deletingLastStory = Boolean(storyPendingDeletion) && answered.length === 1

  const deletePendingStory = () => {
    if (deletingLastStory) {
      setStoryPendingDeletion(null)
      deleteStory()

      return
    }

    props.removeStory(storyPendingDeletion['prompt-id'])
    setStoryPendingDeletion(null)
    props.toast('Story deleted')
  }

  const chapterTile = chapter => (
    <button className='ls-topic-card' key={chapter.key} onClick={() => props.openChapter(chapter.key)}>
      <span className='ls-topic-name'>{chapter.name}</span>
      <span className='ls-topic-sub'>{chapter.description}</span>
    </button>
  )

  const preview = () => {
    Logger.log({name: 'entered_life_story', payload: {context: 'life_story_review', type: 'vault', wildcard: 'owner'}})
    routerUtils.push(lifeStoryStoryPath({ethicalWillId: props.ethicalWillId}))
  }

  const published = Boolean(firstGroupId)

  return (
    <div className='ls-review review-screen'>
      <div className='ls-review-bar'>
        <button className='ls-crumb' onClick={props.exit}>&larr; Back to my Everplan</button>
        {
          published &&
            <div className='ls-kebab-wrap' ref={menuRef}>
              <button
                aria-expanded={menuOpen}
                aria-label='More options'
                className='ls-kebab'
                onClick={() => setMenuOpen(!menuOpen)}>
                <KebabIcon />
              </button>
              {
                // A plain popover of buttons rather than an ARIA menu: menu
                // roles promise arrow-key navigation the popover doesn't have.
                menuOpen &&
                  <div className='ls-menu-pop'>
                    {
                      pdfFile &&
                        <button onClick={downloadPdf}>
                          <PdfIcon />
                          Download story as PDF
                        </button>
                    }
                    {
                      audioFiles.length > 0 &&
                        <button onClick={downloadAudio}>
                          <MicIcon size={16} />
                          Download audio files
                        </button>
                    }
                    <button
                      className='delete-icon-button'
                      onClick={() => { setMenuOpen(false); setConfirmingDelete(true) }}>
                      <DeleteIcon className='icon delete-icon' fill='currentColor' width={12} />
                      Delete entire story...
                    </button>
                  </div>
              }
            </div>
        }
      </div>
      <div className='ls-item-card'>
        <div className='ls-item-head'>
          <div>
            <h2>{props.title}</h2>
            <div className='ls-item-meta'>{`Started ${startedDate}`}</div>
            <p className='ls-head-note'>
              Whenever you add or edit a story, your PDF and recordings refresh automatically &mdash; your
              loved ones will always receive the latest version. Write in any order you like: chapters
              fall into place in your finished story.
            </p>
          </div>
          <div className='ls-item-head-actions'>
            <Button className='ls-btn ls-btn-primary ls-btn-sm' onClick={props.continueStory}>
              Continue my story
            </Button >
          </div>
        </div>

        {
          chapters.length > 0 &&
            <div className='ls-item-section'>
              <h4>Your stories</h4>
              <div className='ls-review-chapters'>
                {
                  chapters.map(({chapter, stories: chapterStories}) => (
                    <div
                      className={`ls-review-chapter${addStoryChapterKey === chapter.key ? ' open' : ''}`}
                      key={chapter.key}>
                      <div className='ls-review-chapter-head'>
                        <span className='ls-review-chapter-name'>{chapter.name}</span>
                        <span className='ls-review-chapter-count'>{storyCountLabel(chapterStories.length)}</span>
                      </div>
                      {
                        chapterStories.map(story => (
                          <div className='ls-story-row' key={story['prompt-id']}>
                            <div className='ls-story-row-body'>
                              <div className='ls-story-row-question'>{story.question}</div>
                              {
                                story.recording &&
                                  <div className='ls-story-row-sub'>
                                    <span className='ls-story-row-voice'>
                                      <MicIcon size={13} strokeWidth={2} /> {formatDuration(story.recording.duration)}
                                    </span>
                                  </div>
                              }
                            </div>
                            <IconButton
                              aria-label='Edit this story'
                              className='icon-button'
                              onClick={() => props.revisitPrompt(story['prompt-id'])}>
                              <EditIcon className='icon' fill='currentColor' width={12} />
                            </IconButton>
                            <IconButton
                              aria-label='Delete this story'
                              className='icon-button'
                              onClick={() => setStoryPendingDeletion(story)}>
                              <DeleteIcon className='icon' fill='currentColor' width={12} />
                            </IconButton>
                          </div>
                        ))
                      }
                      <AddStoryDrawer
                        chapter={chapter}
                        open={addStoryChapterKey === chapter.key}
                        openPrompt={props.openPrompt}
                        stories={props.stories}
                        toggle={() => setAddStoryChapterKey(openKey => (openKey === chapter.key ? null : chapter.key))}
                      />
                    </div>
                  ))
                }
              </div>
            </div>
        }

        {
          untouchedChapters.length > 0 ?
            <div className='ls-item-section'>
              <h4>Begin a new chapter</h4>
              <p className='ls-tile-lede'>Wherever a memory pulls you &mdash; there&rsquo;s no order and no finish line.</p>
              <div className='ls-topic-grid ls-topic-grid-review'>
                {untouchedChapters.slice(0, VISIBLE_UNTOUCHED_CHAPTERS).map(chapterTile)}
              </div>
              {
                // Once every untouched chapter fits in the visible row, there
                // is nothing left to fold away — an always-rendered toggle
                // would just animate open an empty grid.
                untouchedChapters.length > VISIBLE_UNTOUCHED_CHAPTERS &&
                  <>
                    <div className={`ls-tile-extra${showAllChapters ? ' open' : ''}`}>
                      <div className='ls-tile-extra-inner'>
                        <div className='ls-topic-grid ls-topic-grid-review'>
                          {untouchedChapters.slice(VISIBLE_UNTOUCHED_CHAPTERS).map(chapterTile)}
                        </div>
                      </div>
                    </div>
                    <button
                      aria-expanded={showAllChapters}
                      className='ls-toc-more'
                      onClick={() => setShowAllChapters(!showAllChapters)}>
                      <span>{showAllChapters ? 'Fewer chapters' : 'More chapters'}</span>
                      <CaretIcon />
                    </button>
                  </>
              }
            </div> :
            <div className='ls-item-section'>
              <p className='ls-tile-lede ls-review-note'>
                Beautiful work. You&rsquo;ve begun every chapter.
              </p>
            </div>
        }

        {
          published &&
            <div className='ls-item-section ls-review-share-row'>
              <button className='ls-btn ls-btn-outline ls-btn-sm' onClick={preview}>Preview</button>
              <span className='ls-review-share-copy'>
                See your story as your loved ones will receive it &mdash; then share it when you&rsquo;re ready.
              </span>
              {
                itemResponse &&
                  <ManagePermissionsContainer
                    context='life_story_review'
                    firmConfig={firmConfig}
                    firstGroupId={firstGroupId}
                    itemResponse={itemResponse}
                    keyField={props.title}
                    openAddDeputyModal={openAddDeputyModal}
                    userConfig={props.userConfig}
                    onToggleManagePermissionsModal={noop}
                  />
              }
            </div>
        }
      </div>

      {
        confirmingDelete &&
          <div className='ls-overlay' role='dialog' aria-modal='true' aria-labelledby='ls-delete-title'>
            <div className='ls-overlay-card'>
              <h3 id='ls-delete-title'>Delete your entire story?</h3>
              <p>This deletes every story, every recording, and removes access for anyone you&rsquo;ve shared it with.</p>
              <div className='ls-overlay-actions space-between'>
                <button className='ls-link-danger' onClick={deleteStory}>Delete everything</button>
                <button className='ls-btn ls-btn-primary ls-btn-sm' onClick={() => setConfirmingDelete(false)}>Keep my story</button>
              </div>
            </div>
          </div>
      }
      {
        storyPendingDeletion &&
          <div className='ls-overlay' role='dialog' aria-modal='true' aria-labelledby='ls-delete-story-title'>
            <div className='ls-overlay-card'>
              <h3 id='ls-delete-story-title'>{deletingLastStory ? 'Delete your last story?' : 'Delete this story?'}</h3>
              <p>
                {
                  deletingLastStory ?
                    'This is your last story, so deleting it removes your entire My Life Story, including its recordings.' :
                    'This deletes the story and its recording. The rest of your story is untouched.'
                }
              </p>
              <div className='ls-overlay-actions space-between'>
                <button className='ls-link-danger' onClick={deletePendingStory}>Delete story</button>
                <button className='ls-btn ls-btn-primary ls-btn-sm' onClick={() => setStoryPendingDeletion(null)}>Keep this story</button>
              </div>
            </div>
          </div>
      }
      {
        showAddDeputyModal &&
          <AddDeputyModal
            context='life_story_review'
            deputies={deputies}
            exit={closeAddDeputyModal}
            itemResponses={itemResponses}
            newContacts={newContacts}
            selectedContact={contactToAddAsDeputy}
            source={contactToAddAsDeputy.isEmpty() ? null : 'suggested'}
            userConfig={props.userConfig}
          />
      }
    </div>
  )
}

ReviewScreen.propTypes = {
  continueStory: PropTypes.func,
  ethicalWill: PropTypes.instanceOf(Map),
  ethicalWillId: PropTypes.string,
  exit: PropTypes.func,
  openChapter: PropTypes.func,
  openPrompt: PropTypes.func,
  removeStory: PropTypes.func,
  revisitPrompt: PropTypes.func,
  stories: PropTypes.array,
  title: PropTypes.string,
  toast: PropTypes.func,
  userConfig: PropTypes.instanceOf(Map)
}

export default ReviewScreen
